import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FilePdf, CalendarBlank, Airplane, Images, CircleNotch, DownloadSimple, EnvelopeSimple, Receipt } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Loading, EmptyState } from "@/components/riq";

async function pollReportJob(jobId, tries = 100) {
  for (let i = 0; i < tries; i++) {
    await new Promise((r) => setTimeout(r, 3000));
    try {
      const { data } = await api.get(`/reports/jobs/${jobId}`);
      if (data.status !== "pending") return data;
    } catch { /* keep polling on transient errors */ }
  }
  return null;
}


async function downloadPdf(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  const blob = new Blob([res.data], { type: "application/pdf" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

export default function Reports() {
  const { user } = useAuth();
  const [opts, setOpts] = useState(null);
  const [tab, setTab] = useState("month");
  const [month, setMonth] = useState("");
  const [tripId, setTripId] = useState("");
  const [includeImages, setIncludeImages] = useState(false);
  const [busy, setBusy] = useState(false);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    api.get("/reports/options").then((r) => {
      setOpts(r.data);
      if (r.data.months[0]) setMonth(r.data.months[0].value);
      if (r.data.trips[0]) setTripId(r.data.trips[0].id);
    }).catch(() => setOpts(false));
  }, []);

  const generate = async () => {
    setBusy(true);
    try {
      if (tab === "month") {
        if (!month) { toast.error("Pick a month"); return; }
        await downloadPdf(`/reports/monthly.pdf?month=${month}&images=${includeImages}`, `sliply_report_${month}.pdf`);
      } else {
        if (!tripId) { toast.error("Pick a trip"); return; }
        await downloadPdf(`/reports/trip.pdf?trip_id=${tripId}&images=${includeImages}`, `sliply_trip.pdf`);
      }
      toast.success("Report downloaded");
    } catch { toast.error("Could not generate report"); }
    finally { setBusy(false); }
  };

  const emailReport = async () => {
    setEmailing(true);
    try {
      let res;
      if (tab === "month") {
        if (!month) { toast.error("Pick a month"); return; }
        res = await api.post(`/reports/monthly/email?month=${month}&images=${includeImages}`);
      } else {
        if (!tripId) { toast.error("Pick a trip"); return; }
        res = await api.post(`/reports/trip/email?trip_id=${tripId}&images=${includeImages}`);
      }
      if (!res.data?.job_id) { toast.error(res.data?.error || "Could not email the report"); return; }
      toast.message("Preparing your report — we'll email it shortly");
      const job = await pollReportJob(res.data.job_id);
      if (job?.status === "done" && job.link_only) toast.success("Report was large, so we emailed you a download link");
      else if (job?.status === "done") toast.success(`Report emailed to ${res.data.email || "your inbox"}`);
      else if (job?.status === "failed") toast.error(job.error || "Could not email the report");
      else toast.message("Still working on it — the report will arrive in your inbox soon");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not email the report");
    } finally { setEmailing(false); }
  };

  if (opts === false) return <EmptyState icon={FilePdf} title="Reports unavailable" hint="Please try again later." />;
  if (!opts) return <Loading />;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="reports-page">
      <div>
        <h1 className="font-head text-3xl font-bold tracking-tight text-riq-primary">Reports</h1>
        <p className="text-muted-foreground mt-1">Export a PDF of your spending. Pick a month or a trip and choose whether to attach the receipt images.</p>
      </div>

      <div className="flex gap-2">
        {[["month", "Monthly report", CalendarBlank], ["trip", "Trip report", Airplane]].map(([k, lbl, Icon]) => (
          <button key={k} data-testid={`reports-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === k ? "bg-riq-primary text-white" : "bg-secondary text-neutral-600"}`}>
            <Icon size={16} weight="bold" /> {lbl}
          </button>
        ))}
      </div>

      <Card>
        {tab === "month" ? (
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Month</span>
            {opts.months.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">No receipts yet — add some to generate a report.</p>
            ) : (
              <select data-testid="reports-month-select" value={month} onChange={(e) => setMonth(e.target.value)}
                className="mt-1 w-full bg-secondary/60 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-riq-primary/20">
                {opts.months.map((m) => <option key={m.value} value={m.value}>{m.label} · {m.count} receipt{m.count === 1 ? "" : "s"}</option>)}
              </select>
            )}
          </label>
        ) : (
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Trip</span>
            {opts.trips.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">No trips yet. Create one on the Trips page and assign receipts to it.</p>
            ) : (
              <select data-testid="reports-trip-select" value={tripId} onChange={(e) => setTripId(e.target.value)}
                className="mt-1 w-full bg-secondary/60 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-riq-primary/20">
                {opts.trips.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.receipt_count} receipt{t.receipt_count === 1 ? "" : "s"}</option>)}
              </select>
            )}
          </label>
        )}

        <button data-testid="reports-images-toggle" onClick={() => setIncludeImages((v) => !v)}
          className="mt-4 flex items-center justify-between w-full rounded-xl border border-riq-border px-4 py-3 text-left">
          <span className="flex items-center gap-2 text-sm font-medium"><Images size={18} weight="duotone" className="text-riq-primary" /> Attach receipt images</span>
          <span className={`relative h-6 w-11 rounded-full transition-colors ${includeImages ? "bg-riq-primary" : "bg-neutral-300"}`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${includeImages ? "left-[22px]" : "left-0.5"}`} />
          </span>
        </button>
        <p className="mt-1.5 text-xs text-muted-foreground">Turning this on embeds a picture of each slip — larger file, but great for expense claims.</p>

        <button data-testid="reports-generate-btn" onClick={generate} disabled={busy || emailing}
          className="mt-5 flex items-center justify-center gap-2 w-full rounded-full bg-riq-primary text-white py-3 font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-60">
          {busy ? <CircleNotch className="animate-spin" size={18} /> : <><DownloadSimple size={18} weight="bold" /> Generate PDF</>}
        </button>

        <button data-testid="reports-email-btn" onClick={emailReport} disabled={busy || emailing}
          className="mt-3 flex items-center justify-center gap-2 w-full rounded-full border border-riq-border bg-white text-riq-primary py-3 font-medium hover:border-riq-primary hover:-translate-y-0.5 transition-all disabled:opacity-60">
          {emailing ? <CircleNotch className="animate-spin" size={18} /> : <><EnvelopeSimple size={18} weight="duotone" /> Email me this report</>}
        </button>
        <p className="mt-1.5 text-xs text-muted-foreground text-center">We'll send the PDF to {user?.email}.</p>
      </Card>

      {user?.account_type === "business" && tab === "month" && month && (
        <VatReclaimCard month={month} />
      )}
    </div>
  );
}

function VatReclaimCard({ month }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/reports/vat-reclaim?month=${month}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [month]);

  const fmt = (c, v) => `${c} ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Card data-testid="vat-reclaim-card">
      <div className="flex items-center gap-2 mb-1"><Receipt size={20} weight="duotone" className="text-riq-primary" /><h3 className="font-head font-semibold text-riq-primary">VAT reclaim</h3></div>
      <p className="text-sm text-muted-foreground mb-4">Reclaimable VAT summed per expense category for this month — handy at tax time.</p>
      {data === null ? <p className="text-sm text-muted-foreground">Loading…</p>
        : data === false ? <p className="text-sm text-muted-foreground">Couldn't load VAT summary.</p>
        : data.categories.length === 0 ? <p className="text-sm text-muted-foreground">No receipts this month.</p>
        : (
          <>
            <div className="rounded-2xl bg-riq-primary text-white px-5 py-4 mb-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/60">Total reclaimable VAT</div>
              <div data-testid="vat-total" className="font-head text-3xl font-bold mt-1">{fmt(data.currency, data.totals.vat)}</div>
              <div className="text-xs text-white/70 mt-1">Net {fmt(data.currency, data.totals.net)} · Gross {fmt(data.currency, data.totals.gross)} · {data.totals.count} receipt{data.totals.count === 1 ? "" : "s"}</div>
            </div>
            <div className="overflow-hidden rounded-xl border border-riq-border">
              <table className="w-full text-sm">
                <thead><tr className="bg-secondary/60 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="text-left px-3 py-2 font-semibold">Category</th>
                  <th className="text-right px-3 py-2 font-semibold">Net</th>
                  <th className="text-right px-3 py-2 font-semibold">VAT</th>
                  <th className="text-right px-3 py-2 font-semibold">Gross</th>
                </tr></thead>
                <tbody>
                  {data.categories.map((c) => (
                    <tr key={c.category} data-testid={`vat-row-${c.category}`} className="border-t border-riq-border">
                      <td className="px-3 py-2">{c.category} <span className="text-muted-foreground">· {c.count}</span></td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt(data.currency, c.net)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-riq-primary">{fmt(data.currency, c.vat)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt(data.currency, c.gross)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
    </Card>
  );
}
